
import React from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { VariableCategory } from "@/types/variables";

const variableCategories: VariableCategory[] = [
  'Business Core',
  'Contact Information',
  'Business Hours',
  'Media & Assets',
  'Services & Products',
  'Page Content',
  'About Section',
  'Gallery',
  'Contact',
  'Social Media',
  'Blog',
  'SEO & Marketing',
  'Customer Service',
  'Team & Staff',
  'Policies & Legal',
  'Application Information',
];

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  description: z.string().min(5, { message: "Description must be at least 5 characters." }),
  value: z.string(),
  category: z.enum([
    'Business Core',
    'Contact Information',
    'Business Hours',
    'Media & Assets',
    'Services & Products',
    'Page Content',
    'About Section',
    'Gallery',
    'Contact',
    'Social Media',
    'Blog',
    'SEO & Marketing',
    'Customer Service',
    'Team & Staff',
    'Policies & Legal',
    'Application Information',
  ] as const),
});

interface VariableFormProps {
  onSubmit: (values: z.infer<typeof formSchema>) => void;
  initialValues?: z.infer<typeof formSchema>;
}

const VariableForm: React.FC<VariableFormProps> = ({ onSubmit, initialValues }) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues || {
      name: "",
      description: "",
      value: "",
      category: "Business Core",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Variable Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Business Name" {...field} />
              </FormControl>
              <FormDescription>
                The name of the variable for internal reference.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Brief description of what this variable is used for" {...field} />
              </FormControl>
              <FormDescription>
                Help content editors understand what this variable represents.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Value</FormLabel>
              <FormControl>
                <Input placeholder="Value of the variable" {...field} />
              </FormControl>
              <FormDescription>
                The actual content that will be injected in your templates.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {variableCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Group similar variables together for easier management.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">Save Variable</Button>
      </form>
    </Form>
  );
};

export default VariableForm;
